import type { ApiDocument, ApiFolder } from "@workith/shared";
import { Badge } from "@workith/ui";
import { Folder } from "lucide-react";
import { DocumentRow } from "./DocumentRow";
import { DocumentVisibilityBadge } from "./DocumentVisibilityBadge";
import { canDeleteFolder, canRenameFolder, FolderActions } from "./FolderActions";

interface DriveItemListProps {
  folders: ApiFolder[];
  documents: ApiDocument[];
  allFolders: ApiFolder[];
  onOpenFolder: (folder: ApiFolder) => void;
  onOpenDocument: (document: ApiDocument) => void;
  onFolderUpdate: () => void;
  onDocumentUpdate: () => void;
}

function canMutateDriveFolder(folder: ApiFolder): boolean {
  return canRenameFolder(folder) || canDeleteFolder(folder);
}

export function DriveItemList({
  folders,
  documents,
  allFolders,
  onOpenFolder,
  onOpenDocument,
  onFolderUpdate,
  onDocumentUpdate,
}: DriveItemListProps) {
  return (
    <div className="overflow-hidden rounded-lg border bg-card" data-testid="drive-item-list">
      <div className="hidden grid-cols-[minmax(0,1fr)_140px_120px_40px] gap-3 border-b bg-muted/40 px-4 py-2 text-xs font-medium text-muted-foreground md:grid">
        <span>Name</span>
        <span>Type</span>
        <span>Modified</span>
        <span aria-hidden="true" />
      </div>

      {folders.map((folder) => (
        <div
          key={folder.id}
          className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 border-b px-4 py-3 md:grid-cols-[minmax(0,1fr)_140px_120px_40px]"
          data-testid={`drive-item-folder-${folder.id}`}
        >
          <button
            type="button"
            className="flex min-w-0 items-center gap-3 text-left"
            onClick={() => onOpenFolder(folder)}
            aria-label={`Open folder ${folder.name}`}
          >
            <Folder className="h-5 w-5 shrink-0 text-muted-foreground" />
            <span className="min-w-0">
              <span className="block truncate font-medium">{folder.name}</span>
              <span className="mt-1 block text-xs text-muted-foreground md:hidden">Folder</span>
            </span>
          </button>

          <div className="hidden items-center md:flex">
            <Badge variant="secondary">Folder</Badge>
          </div>
          <span className="hidden text-sm text-muted-foreground md:block">-</span>
          <div className="flex justify-end">
            {canMutateDriveFolder(folder) ? (
              <FolderActions folder={folder} onUpdate={onFolderUpdate} />
            ) : null}
          </div>
        </div>
      ))}

      {documents.map((documentItem) => (
        <DocumentRow
          key={documentItem.id}
          document={documentItem}
          folders={allFolders}
          onOpenDocument={onOpenDocument}
          renderBadge={(documentItem) => (
            <DocumentVisibilityBadge visibility={documentItem.visibility} />
          )}
          onDocumentUpdate={onDocumentUpdate}
          rowTestId={`drive-item-document-${documentItem.id}`}
          mobileBadgeTestId={`drive-item-document-mobile-badge-${documentItem.id}`}
        />
      ))}
    </div>
  );
}
